import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import Euler, { EulerViewProps } from '@euler/components/layout';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useCallback, useRef, useState } from 'react';
import {
  LayoutChangeEvent,
  LayoutRectangle,
  StyleProp,
  TextStyle,
} from 'react-native';
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { ExtendedPropSummaryItem } from './types';

const kCaretSize = 24;
const kDefaultColor = '#333';

export const ExtendedPropsInfo = memo(
  ({ items }: { items: ExtendedPropSummaryItem[] }) => {
    const { rpx } = useResponsiveContext();

    // animation control values
    const rotated = useSharedValue(0);
    const collapsed = useRef(false);
    const animating = useRef(false);

    const [layout, setLayout] = useState<LayoutRectangle>();

    const animatedStyle = useAnimatedStyle(() => {
      const opacity = interpolate(rotated.value, [0, 1], [1, 0]);
      const height = interpolate(
        rotated.value,
        [0, 1],
        [layout?.height ?? 0, 0],
      );
      return layout?.height == null
        ? {}
        : {
            opacity,
            height,
          };
    });

    const caretAnimatedStyle = useAnimatedStyle(() => {
      const angle = interpolate(rotated.value, [0, 1], [-180, 0]);
      return {
        transform: [
          {
            rotate: `${angle}deg`,
          },
        ],
      };
    });

    const onExpandOrCollapse = useCallback(() => {
      collapsed.current = !collapsed.current;
      animating.current = true;
      rotated.value = withTiming(
        rotated.value ? 0 : 1,
        {
          duration: 250,
          easing: Easing.in(Easing.linear),
        },
        () => {
          animating.current = false;
        },
      );
    }, [rotated]);

    const onLayout = useCallback((e: LayoutChangeEvent) => {
      if (collapsed.current || animating.current) {
        return;
      }
      setLayout(e.nativeEvent.layout);
    }, []);

    if (!items.length) {
      return null;
    }
    return (
      <Euler.View>
        <Animated.View style={animatedStyle}>
          <Euler.View
            onLayout={onLayout}
            flexDirection="row"
            flexWrap="wrap"
            justifyContent="flex-start"
            paddingVertical={rpx(10)}
            backgroundColor={'#f7f7f7'}
            borderTopWidth={1}
            borderTopColor={'#eee'}
          >
            {items.map(
              ({ id, label, text, textStyle, textSegments, remark, color }) => (
                <ExtendedPropInfoCell
                  key={id}
                  label={label}
                  text={text}
                  textStyle={textStyle}
                  textSegments={textSegments}
                  remark={remark}
                  paddingHorizontal={rpx(15)}
                  marginVertical={rpx(5)}
                  minWidth={'25%'}
                  color={color}
                />
              ),
            )}
          </Euler.View>
        </Animated.View>
        <Center
          position="absolute"
          left={0}
          right={0}
          top={-rpx(kCaretSize) / 2 + 1}
        >
          <Euler.View
            width={rpx(kCaretSize)}
            height={rpx(kCaretSize) / 2}
            overflow="hidden"
          >
            <Center.Touchable
              backgroundColor="#f7f7f7"
              width={rpx(kCaretSize)}
              height={rpx(kCaretSize)}
              borderRadius={rpx(kCaretSize)}
              hitSlop={PredefinedHitSlops.extraLarge}
              onPress={onExpandOrCollapse}
            >
              <Animated.View
                style={[caretAnimatedStyle, { top: -rpx(kCaretSize * 0.24) }]}
              >
                <Icon
                  name="chevron-down"
                  size={rpx(kCaretSize * 0.5)}
                  color="#ccc"
                />
              </Animated.View>
            </Center.Touchable>
          </Euler.View>
        </Center>
      </Euler.View>
    );
  },
);

export const ExtendedPropInfoCell = memo(
  ({
    label,
    text,
    textStyle,
    remark,
    textSegments,
    color,
    ...props
  }: {
    label: string;
    text?: string;
    remark?: string;
    textStyle?: StyleProp<TextStyle>;
    textSegments?: { text: string; color?: string }[];
    color?: string;
  } & EulerViewProps) => {
    const { rpx } = useResponsiveContext();
    return (
      <Euler.View {...props}>
        <Typography.Text size={rpx(9)} lineHeight={rpx(16)} bold color="#999">
          {label}
        </Typography.Text>
        <Euler.View flexDirection="row" flexWrap="wrap">
          {textSegments?.length ? (
            textSegments.map((seg, index) => (
              <Euler.View
                key={index}
                borderWidth={0}
                borderStyle="solid"
                borderBottomWidth={rpx(1)}
                borderBottomColor="#ddd"
                mr={rpx(5)}
              >
                <Typography.Text
                  regular
                  color={seg.color ?? kDefaultColor}
                  size={rpx(12)}
                  lineHeight={rpx(20)}
                >
                  {seg.text}
                </Typography.Text>
              </Euler.View>
            ))
          ) : (
            <Typography.Text
              regular
              color={color ?? kDefaultColor}
              size={rpx(12)}
              lineHeight={rpx(20)}
              style={textStyle}
            >
              {text ?? '-'}
            </Typography.Text>
          )}
        </Euler.View>
        {remark?.trim() ? (
          <Typography.Text
            regular
            size={rpx(10)}
            lineHeight={18}
            numberOfLines={0}
            decorationLine="underline"
            decorationStyle="dotted"
            color="#888"
          >{`备注: ${remark.trim()}`}</Typography.Text>
        ) : null}
      </Euler.View>
    );
  },
);
